import { Money } from "@quint/components";
import { formatBigNumber } from "@quint/services/web3";
import {
  conventionalBnbPoolV2Address,
  useConventionalBnbPoolV2AccountDetails,
  useConventionalBnbPoolV2CalculateLpReward,
  useConventionalBnbPoolV2Harvest,
  useConventionalBnbPoolV2Stake,
  useConventionalBnbPoolV2StakingDetails,
  useConventionalBnbPoolV2Unstake,
  useConventionalBnbPoolV2UnstakePartial,
  usePrepareConventionalBnbPoolV2Harvest,
  usePrepareConventionalBnbPoolV2Stake,
  usePrepareConventionalBnbPoolV2Unstake,
  usePrepareConventionalBnbPoolV2UnstakePartial,
} from "@quint/services/web3/hooks/conventional-bnb-pool-v2";
import { useLpAllowance, useLpApprove, useLpBalanceOf, usePrepareLpApprove } from "@quint/services/web3/hooks/lp";
import { parseError } from "@quint/services/web3/utils/parse-error";
import { useToast } from "@quint/ui/toast";
import { BigNumber } from "ethers";
import { parseUnits } from "ethers/lib/utils.js";
import { toBn } from "evm-bn";
import { useCallback, useState } from "react";
import { useAccount, useWaitForTransaction } from "wagmi";
import Staking from "../staking";

export const QuintBNBV2Rewards = () => {
  const { address } = useAccount();
  const { data, isLoading } = useConventionalBnbPoolV2CalculateLpReward({
    args: [address!],
    enabled: Boolean(address),
    watch: true,
  });
  return <Money value={formatBigNumber(data)} isLoading={isLoading} />;
};

export const QuintBNBV2CurrentlyStaked = () => {
  const { address } = useAccount();
  const { data: tokenAmountData, isLoading: isTokenAmountLoading } = useConventionalBnbPoolV2StakingDetails({
    args: [address!],
    enabled: Boolean(address),
    watch: true,
  });

  const { data: rewardsData, isLoading: isRewardsLoading } = useConventionalBnbPoolV2CalculateLpReward({
    args: [address!],
    enabled: Boolean(address),
    watch: true,
  });
  const isLoading = isTokenAmountLoading || isRewardsLoading;
  const currentlyStaked = !isLoading
    ? BigNumber.from(tokenAmountData?.tokenAmount || 0).add(BigNumber.from(rewardsData || 0))
    : 0;
  return <Money value={formatBigNumber(currentlyStaked)} isLoading={isLoading} />;
};

export const QuintBNBV2TotalHarvested = () => {
  const { address } = useAccount();
  const { data, isLoading } = useConventionalBnbPoolV2AccountDetails({
    args: [address!],
    enabled: Boolean(address),
    watch: true,
  });
  return <Money value={formatBigNumber(data?.totalUnstakedLPs)} isLoading={isLoading} />;
};

export const QuintBNBV2HarvestButton = () => {
  const { enqueueToast } = useToast();
  const { config, error, isLoading: isPreparing } = usePrepareConventionalBnbPoolV2Harvest();

  const { write, data, isLoading: isWriting } = useConventionalBnbPoolV2Harvest(config);
  const onClick = useCallback(() => {
    if (error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Harvest",
        message: parseError(error),
      });
      return;
    }
    if (!write) {
      enqueueToast({
        variant: "error",
        title: "Preparing...",
        message: "Please wait we are preparing your data",
      });
      return;
    }
    write?.();
  }, [enqueueToast, error, write]);

  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      enqueueToast({
        variant: "success",
        title: "Harvest Success",
        message: "Successfully harvested your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Harvest",
        message: parseError(err),
      });
    },
  });
  const loading = isPreparing || isWriting || isWaiting;
  return <Staking.HarvestButton deprecated loading={loading} onClick={onClick} />;
};
type Props = {
  value: number;
  fullUnstake: boolean;
};

const FullUnstakeButton = (props: Props) => {
  const { fullUnstake } = props;
  const { enqueueToast } = useToast();
  const {
    config,
    error,
    isLoading: isPreparing,
  } = usePrepareConventionalBnbPoolV2Unstake({
    enabled: fullUnstake,
  });

  const { write, data, isLoading: isWriting } = useConventionalBnbPoolV2Unstake(config);
  const onClick = useCallback(() => {
    if (error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: parseError(error),
      });
      return;
    }
    if (!write) {
      enqueueToast({
        variant: "error",
        title: "Preparing...",
        message: "Please wait we are preparing your data",
      });
      return;
    }
    write?.();
  }, [enqueueToast, error, write]);

  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      enqueueToast({
        variant: "success",
        title: "Unstake Success",
        message: "Successfully unstaked your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: parseError(err),
      });
    },
  });
  const loading = isPreparing || isWriting || isWaiting;

  return <Staking.UnstakeDialogSubmitButton fullUnstake={fullUnstake} disabled={loading} onClick={onClick} />;
};
const getIsValidAmount = (amount: string, decimals: number) => {
  try {
    toBn(amount, decimals);
    return true;
  } catch (err) {
    return false;
  }
};

const PartialUnstakeButton = (props: Props) => {
  const { value, fullUnstake } = props;
  const { address } = useAccount();
  const tokenDecimals = 18;
  const isValidInput = value > 0 && getIsValidAmount(`${value}`, tokenDecimals);
  const tokenAmount = isValidInput ? toBn(`${value}`, tokenDecimals) : toBn("0", tokenDecimals);
  const isZeroAmount = BigNumber.from(tokenAmount).isZero();

  const { data: rewards, isLoading: isCheckingRewards } = useConventionalBnbPoolV2CalculateLpReward({
    args: [address!],
    enabled: Boolean(address),
  });
  const isRewardsHarvested = +formatBigNumber(rewards) < 0.1;
  const { enqueueToast } = useToast();
  const {
    config,
    error,
    isLoading: isPreparing,
  } = usePrepareConventionalBnbPoolV2UnstakePartial({
    args: [parseUnits(value.toString())],
    enabled: Boolean(address) && isRewardsHarvested && !isZeroAmount && isValidInput,
  });

  const {
    write,
    data,
    isLoading: isWriting,
  } = useConventionalBnbPoolV2UnstakePartial({
    ...config,
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: parseError(err),
      });
    },
  });
  const onClick = useCallback(() => {
    if (!isRewardsHarvested) {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: "You must harvest your rewards before partial unstaking",
      });
      return;
    }
    if (!isValidInput || isZeroAmount) {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: "Please provide a valid amount to unstake",
      });
      return;
    }
    if (error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Unstake",
        message: parseError(error),
      });
      return;
    }
    if (!write) {
      enqueueToast({
        variant: "error",
        title: "Preparing...",
        message: "Please wait we are preparing your data",
      });
      return;
    }
    write?.();
  }, [enqueueToast, error, isRewardsHarvested, isValidInput, isZeroAmount, write]);

  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      enqueueToast({
        variant: "success",
        title: "Unstake Success",
        message: "Successfully unstaked your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Harvest",
        message: parseError(err),
      });
    },
  });
  const loading = isPreparing || isWriting || isWaiting || isCheckingRewards;

  return <Staking.UnstakeDialogSubmitButton fullUnstake={fullUnstake} disabled={loading} onClick={onClick} />;
};
const renderUnstakeButton = (value: number, fullUnstake: boolean) => {
  if (fullUnstake) {
    return <FullUnstakeButton value={value} fullUnstake={fullUnstake} />;
  } else {
    return <PartialUnstakeButton value={value} fullUnstake={fullUnstake} />;
  }
};
export const QuintBNBV2UnstakeDialog = () => {
  return (
    <Staking.UnstakeDialog renderUnstakeButton={renderUnstakeButton}>
      <Staking.UnstakeButton />
    </Staking.UnstakeDialog>
  );
};

const QuintBNBV2StakeButton = (props: { value: number; setValue: (v: number) => void }) => {
  const { value = 0, setValue } = props;
  const { address } = useAccount();

  const { enqueueToast } = useToast();
  const [localLoading, setLocalLoading] = useState(false);
  const { data: balance, isLoading: isBalanceLoading } = useLpBalanceOf({
    args: [address!],
    enabled: !Boolean(address),
  });
  balance;
  const tokenDecimals = 18;
  const isValidInput = value > 0 && getIsValidAmount(`${value}`, tokenDecimals);
  const tokenAmount = isValidInput ? toBn(`${value}`, tokenDecimals) : toBn("0", tokenDecimals);
  const isZeroAmount = BigNumber.from(tokenAmount).isZero();
  const isEnoughBalance = balance?.gte(tokenAmount);
  const isValueInRange = tokenAmount?.gte(toBn("100", tokenDecimals));

  const isValidValue = isValidInput && !isZeroAmount && isValueInRange && isEnoughBalance && !isBalanceLoading;
  const { data: allowance = 0, isLoading: isLoadingAllowance } = useLpAllowance({
    args: [address!, conventionalBnbPoolV2Address],
    enabled: Boolean(address) && isValidValue,
  });
  const hasAllowance = BigNumber.from(allowance || "0").gte(tokenAmount);

  const {
    config: prepareApproveConfig,

    isLoading: isPreparingApproving,
  } = usePrepareLpApprove({
    args: [conventionalBnbPoolV2Address, balance || parseUnits(value.toString(), tokenDecimals)],
    enabled: Boolean(address) && !hasAllowance && isValidValue,
  });

  const {
    data: approveAllowanceResult,
    writeAsync: approveQuints,
    isLoading: isApproving,
  } = useLpApprove({
    ...prepareApproveConfig,
    onError() {
      setLocalLoading(false);
    },
  });
  const { isLoading: isApprovingAllowance } = useWaitForTransaction({
    hash: approveAllowanceResult?.hash,
    onSuccess() {
      enqueueToast({
        variant: "success",
        title: "Amount Approved Successfully",
        message: "Starting staking...",
      });
    },
    onError(error) {
      enqueueToast({
        variant: "error",
        title: "Failed To Approve Value",
        message: parseError(error),
      });
      setLocalLoading(false);
    },
  });

  const { error: prepareError, isLoading: isPreparing } = usePrepareConventionalBnbPoolV2Stake({
    args: [tokenAmount],
    enabled: Boolean(address) && isValidValue,
  });

  const {
    write,
    data,

    isLoading: isWriting,
  } = useConventionalBnbPoolV2Stake({
    mode: "recklesslyUnprepared",
    args: [tokenAmount],

    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Stake Value",
        message: parseError(err),
      });
    },
    onSettled() {
      setLocalLoading(false);
    },
  });
  const { isLoading: isWaiting } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      setValue(0);
      enqueueToast({
        variant: "success",
        title: "Stake Success",
        message: "Successfully staked your tokens",
      });
    },
    onError: (err) => {
      enqueueToast({
        variant: "error",
        title: "Failed To Harvest",
        message: parseError(err),
      });
    },
  });
  const onClick = useCallback(async () => {
    if (!isValidInput) {
      enqueueToast({
        variant: "error",
        title: "Failed To Stake",
        message: "Please provide a valid amount to stake",
      });
      return;
    }
    if (!isValueInRange) {
      enqueueToast({
        variant: "error",
        title: "Failed To Stake",
        message: "Please stake at least 100 Quint.",
      });
      return;
    }
    if (!isEnoughBalance) {
      enqueueToast({
        variant: "error",
        title: "Failed To Stake",
        message: "You Don't have enough balance.",
      });
      return;
    }
    if (!hasAllowance) {
      setLocalLoading(true);
      const tx = await approveQuints?.();
      if (tx) {
        await tx.wait();
        write?.();
      }
    } else {
      if (prepareError) {
        enqueueToast({
          variant: "error",
          title: "Failed To Stake",
          message: parseError(prepareError),
        });
        return;
      }
      if (!write) {
        enqueueToast({
          variant: "error",
          title: "Preparing...",
          message: "Please wait we are preparing your data",
        });
        return;
      }
      write?.();
    }
    // console.log("error", error);
  }, [approveQuints, enqueueToast, hasAllowance, isEnoughBalance, isValidInput, isValueInRange, prepareError, write]);

  const loading =
    isWriting ||
    isWaiting ||
    isPreparingApproving ||
    isApproving ||
    isLoadingAllowance ||
    isPreparing ||
    isPreparingApproving ||
    isApprovingAllowance ||
    localLoading;
  return <Staking.StakeButton onClick={onClick as () => void} loading={loading} />;
};
export const renderQuintBNBV2StakeButton = (value: number, setValue: (value: number) => void) => {
  return <QuintBNBV2StakeButton value={value} setValue={setValue} />;
};
